import { newId, type SetType, type TypeIDString } from '@macromaxxing/db'
import { create } from 'zustand'
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware'
import { cursorEquals, type SetCursor } from '~/lib'
import { getRestAlertSubscriptionId } from '~/lib/restAlerts'

/**
 * Ephemeral workout-session state that cannot be derived from server data.
 *
 * The set queue itself is NEVER stored here — timer mode derives it from the live
 * session query (`flattenSets(exerciseGroups)`) on every render, so template
 * edits, reorders, and checklist-mode logging stay in sync. The store only keeps
 * the user's position (`cursor`, stable set identity — not an index), uncommitted
 * edits, and running timers.
 */
export interface WorkoutSessionStore {
	sessionId: TypeIDString<'wks'> | null
	sessionStartedAt: number | null

	/** Position in the workout. Resolved against the live queue at render via `resolveCursorIndex`. */
	cursor: SetCursor | null
	/** Uncommitted weight/reps edits for the cursor set. Absent field = show the live planned value. */
	draft: { weight?: number | null; reps?: number }

	/** Stopwatch for the set being performed. `pausedAt` set = paused. */
	setTimer: { startedAt: number; pausedAt: number | null } | null

	rest: {
		id: TypeIDString<'rnj'>
		startedAt: number
		endAt: number
		total: number
		setType: SetType
	} | null

	/** When the current superset round began (first transition confirm) — credits transition time against the next rest. */
	roundStartedAt: number | null

	// Session lifecycle
	setSession: (session: { id: TypeIDString<'wks'>; startedAt?: number } | null) => void
	reset: () => void

	// Cursor + per-set edit state
	setCursor: (cursor: SetCursor | null) => void
	setDraft: (cursor: SetCursor, patch: Partial<{ weight: number | null; reps: number }>) => void

	// Set stopwatch
	startSet: (cursor: SetCursor) => void
	pauseSet: () => void
	resumeSet: () => void
	stopSet: () => void

	// Rest timer
	startRest: (durationSec: number, setType: SetType) => void
	recordTransition: () => void
	dismissRest: () => void
}

// --- Notification ---

function fireNotification(sessionId: TypeIDString<'wks'> | null) {
	if (navigator.vibrate) navigator.vibrate(200)
	if ('Notification' in window && Notification.permission === 'granted') {
		const timerUrl = sessionId ? `/workouts/sessions/${sessionId}/timer` : '/'
		if ('serviceWorker' in navigator) {
			navigator.serviceWorker.ready.then(reg => {
				reg.showNotification('Rest timer done', {
					body: 'Time for your next set',
					tag: 'rest-timer',
					icon: '/pwa-192x192.png',
					data: { url: timerUrl }
				})
			})
		} else {
			new Notification('Rest timer done', { body: 'Time for your next set', tag: 'rest-timer' })
		}
	}
}

// Local delivery is used only while offline or after server scheduling fails.
let localNotification: { restId: TypeIDString<'rnj'>; timeoutId: ReturnType<typeof setTimeout> } | null = null

export function clearLocalRestNotification(restId?: TypeIDString<'rnj'>) {
	if (localNotification && (restId === undefined || localNotification.restId === restId)) {
		clearTimeout(localNotification.timeoutId)
		localNotification = null
	}
}

export function scheduleLocalRestNotification(
	rest: NonNullable<WorkoutSessionStore['rest']>,
	sessionId: TypeIDString<'wks'> | null
) {
	clearLocalRestNotification()
	const delay = rest.endAt - Date.now()
	if (delay <= 0) return
	const timeoutId = setTimeout(() => {
		if (useWorkoutSessionStore.getState().rest?.id === rest.id) fireNotification(sessionId)
		if (localNotification?.restId === rest.id) localNotification = null
	}, delay)
	localNotification = { restId: rest.id, timeoutId }
}

// --- Initial state ---

const INITIAL_STATE = {
	sessionId: null as TypeIDString<'wks'> | null,
	sessionStartedAt: null as number | null,
	cursor: null as SetCursor | null,
	draft: {} as WorkoutSessionStore['draft'],
	setTimer: null as WorkoutSessionStore['setTimer'],
	rest: null as WorkoutSessionStore['rest'],
	roundStartedAt: null as number | null
}

/** Everything scoped to the previous set is invalid once the cursor moves. */
const movedCursor = (cursor: SetCursor | null) => ({
	cursor,
	draft: {},
	setTimer: null as WorkoutSessionStore['setTimer']
})

// --- Persistence ---
// The store survives page reloads and PWA cold starts (mobile OSes kill backgrounded
// PWAs mid-rest). All timers hold absolute timestamps, so rehydrated countdowns
// resume correctly.

/** Rehydrated timers older than this belong to an abandoned session, not a live one. */
const STALE_TIMER_MS = 30 * 60 * 1000

type PersistedSessionState = Pick<
	WorkoutSessionStore,
	'sessionId' | 'sessionStartedAt' | 'cursor' | 'draft' | 'setTimer' | 'rest' | 'roundStartedAt'
>

// The root route is prerendered at build time where localStorage doesn't exist
const noopStorage: StateStorage = {
	getItem: () => null,
	setItem: () => undefined,
	removeItem: () => undefined
}

// --- Store ---

export const useWorkoutSessionStore = create<WorkoutSessionStore>()(
	persist(
		(set, get) => ({
			...INITIAL_STATE,

			setSession: session => {
				if (session === null) {
					clearLocalRestNotification()
					set({ ...INITIAL_STATE })
					return
				}
				const startedAt = session.startedAt !== undefined ? { sessionStartedAt: session.startedAt } : {}
				const current = get().sessionId
				if (current !== null && current !== session.id) {
					// Different session: cursor, edits, and timers belong to the old one
					clearLocalRestNotification()
					set({ ...INITIAL_STATE, sessionId: session.id, ...startedAt })
				} else {
					set({ sessionId: session.id, ...startedAt })
				}
			},

			reset: () => {
				clearLocalRestNotification()
				set({ ...INITIAL_STATE })
			},

			// --- Cursor + per-set edit state ---

			setCursor: cursor => {
				set(movedCursor(cursor))
			},

			setDraft: (cursor, patch) => {
				set(s =>
					cursorEquals(s.cursor, cursor)
						? { draft: { ...s.draft, ...patch } }
						: // Cursor drifted (e.g. the set was removed by a plan edit): snap to the
							// set actually being edited, keeping the running stopwatch.
							{ ...movedCursor(cursor), draft: { ...patch }, setTimer: s.setTimer }
				)
			},

			// --- Set stopwatch ---

			startSet: cursor => {
				set(s => ({
					...(cursorEquals(s.cursor, cursor) ? {} : { cursor, draft: {} }),
					setTimer: { startedAt: Date.now(), pausedAt: null }
				}))
			},

			pauseSet: () => {
				set(s =>
					s.setTimer && s.setTimer.pausedAt === null
						? { setTimer: { ...s.setTimer, pausedAt: Date.now() } }
						: {}
				)
			},

			resumeSet: () => {
				set(s => {
					if (!s.setTimer || s.setTimer.pausedAt === null) return {}
					// Shift the start forward by the paused span so elapsed excludes it
					return {
						setTimer: {
							startedAt: s.setTimer.startedAt + (Date.now() - s.setTimer.pausedAt),
							pausedAt: null
						}
					}
				})
			},

			stopSet: () => {
				set({ setTimer: null })
			},

			// --- Rest timer ---

			startRest: (durationSec, setType) => {
				clearLocalRestNotification()

				const state = get()
				const now = Date.now()

				// Subtract elapsed superset transition time from the countdown — but keep `total`
				// equal to the full duration and backdate `startedAt` so the "rested" readout
				// (total - remaining) reflects time already elapsed during the round.
				const roundElapsedMs = state.roundStartedAt !== null ? Math.max(0, now - state.roundStartedAt) : 0
				const remainingMs = Math.max(0, durationSec * 1000 - roundElapsedMs)
				const endAt = now + remainingMs

				set({
					rest: {
						id: newId('rnj'),
						startedAt: now - roundElapsedMs,
						endAt,
						total: durationSec,
						setType
					},
					roundStartedAt: null
				})
			},

			recordTransition: () => {
				set(s => ({
					roundStartedAt: s.roundStartedAt ?? Date.now()
				}))
			},

			dismissRest: () => {
				clearLocalRestNotification()
				set({ rest: null, roundStartedAt: null })
			}
		}),
		{
			name: 'workout-session',
			storage: createJSONStorage(() => (import.meta.env.SSR ? noopStorage : localStorage)),
			partialize: (s): PersistedSessionState => ({
				sessionId: s.sessionId,
				sessionStartedAt: s.sessionStartedAt,
				cursor: s.cursor,
				draft: s.draft,
				setTimer: s.setTimer,
				rest: s.rest,
				roundStartedAt: s.roundStartedAt
			}),
			merge: (persisted, current) => {
				// JSON boundary: zustand hands back `unknown`; partialize wrote PersistedSessionState
				const restored: WorkoutSessionStore = { ...current, ...(persisted as Partial<PersistedSessionState>) }
				const now = Date.now()
				// Pre-rest-alert persisted state has no identity. Give it one during the
				// backwards-compatible hydration instead of letting an undefined tag leak.
				if (restored.rest && typeof restored.rest.id !== 'string') {
					restored.rest = { ...restored.rest, id: newId('rnj') }
				}
				if (restored.rest && now - restored.rest.endAt > STALE_TIMER_MS) restored.rest = null
				if (restored.setTimer && now - restored.setTimer.startedAt > STALE_TIMER_MS) restored.setTimer = null
				if (restored.roundStartedAt !== null && now - restored.roundStartedAt > STALE_TIMER_MS) {
					restored.roundStartedAt = null
				}
				return restored
			},
			onRehydrateStorage: () => state => {
				// Only the offline path uses page-local delivery. Online rests are owned by
				// the server once accepted and expired rests never fire during hydration.
				if (
					state?.rest &&
					state.rest.endAt > Date.now() &&
					getRestAlertSubscriptionId() !== null &&
					typeof navigator !== 'undefined' &&
					navigator.onLine === false
				) {
					scheduleLocalRestNotification(state.rest, state.sessionId)
				}
			}
		}
	)
)
